import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { KeyValue } from '../../stores';
import { generateId } from '../../lib/utils';
import { extractVariables } from '../../lib/http';
import { VariableQuickLook } from '../variables';

interface KeyValueEditorProps {
  items: KeyValue[];
  onChange: (items: KeyValue[]) => void;
  placeholder?: { key: string; value: string };
  keyReadOnly?: boolean;
  showAddButton?: boolean;
}

export function KeyValueEditor({
  items,
  onChange,
  placeholder,
  keyReadOnly = false,
  showAddButton = true,
}: KeyValueEditorProps) {
  const addItem = () => {
    onChange([
      ...items,
      { key: '', value: '', description: '', enabled: true },
    ]);
  };

  const updateItem = (index: number, updates: Partial<KeyValue>) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], ...updates };
    onChange(newItems);
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="grid grid-cols-[24px_1fr_1fr_32px] gap-2 text-xs font-medium text-muted-foreground px-1">
        <div></div>
        <div>Key</div>
        <div>Value</div>
        <div></div>
      </div>

      {/* Rows */}
      {items.map((item, index) => (
        <div key={index} className="grid grid-cols-[24px_1fr_1fr_32px] gap-2 items-center group">
          {/* Enable Checkbox */}
          <input
            type="checkbox"
            checked={item.enabled}
            onChange={(e) => updateItem(index, { enabled: e.target.checked })}
            className="w-4 h-4 rounded border-input"
          />

          {/* Key Input */}
          <input
            type="text"
            value={item.key}
            onChange={(e) => updateItem(index, { key: e.target.value })}
            placeholder={placeholder?.key || 'Key'}
            readOnly={keyReadOnly}
            className={`px-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring ${
              keyReadOnly ? 'opacity-70 cursor-default' : ''
            }`}
          />

          {/* Value Input with Variable Preview */}
          <div className="relative group/value">
            <input
              type="text"
              value={item.value}
              onChange={(e) => updateItem(index, { value: e.target.value })}
              placeholder={placeholder?.value || 'Value'}
              className="w-full px-3 py-1.5 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {/* Variable preview tooltip */}
            {item.value && extractVariables(item.value).length > 0 && (
              <div className="absolute left-0 top-full mt-1 z-10 hidden group-hover/value:block bg-popover border border-border rounded-md shadow-md px-2 py-1 text-xs">
                <VariableQuickLook text={item.value} className="font-mono" />
              </div>
            )}
          </div>

          {/* Delete Button */}
          <button
            onClick={() => removeItem(index)}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive transition-all"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      {/* Add Row Button */}
      {showAddButton && (
        <button
          onClick={addItem}
          className="flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      )}
    </div>
  );
}
